import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { doInitLU, luHandleAppiError, type U3206InitLU } from './U3206Service';
import { U3206ContextProvider } from './U3206Context';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

export const NS = 'u3206';

export const U3206ROLES = {
  READ: 'U3206:READ',
  ADD: 'U3206:ADD',
  EDIT: 'U3206:EDIT',
  DELETE: 'U3206:DELETE',
  AUDITS: 'U3206:AUDITS',
} as const;
export type U3206ROLES = (typeof U3206ROLES)[keyof typeof U3206ROLES];

const defaultInitLU: U3206InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 50,
  logicalUnitCode: 'U3206',
  providers: [],
  missions: [],
  users: [],
  dataDomains: [],
  dataAccessProfiles: [],
};

const U3206: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3206InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(['u3206'], (err) => {
      if (err) return console.error('Error loading namespace:', err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3206InitLU>({
    doInitLUFn: async () => doInitLU(u2000AxiosAuthenticated),
    onSuccess: (initLU: U3206InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        logicalUnitCode: initLU.logicalUnitCode ?? 'U3206',
        providers: initLU.providers ?? [],
        missions: initLU.missions ?? [],
        users: initLU.users ?? [],
        dataDomains: initLU.dataDomains ?? [],
        dataAccessProfiles: initLU.dataAccessProfiles ?? [],
      });
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t('title', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, setLogicalUnitTitle, setLogicalUnitBreadcrumb, t]);

  if (!i18nReady || !initLUReady) {
    return null;
  }

  return (
    <U3206ContextProvider initLU={initLUData}>
      <Outlet />
    </U3206ContextProvider>
  );
};

export default U3206;
